import {Component, inject} from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import {TitlePagesComponent} from '@/app/shared/components/title-pages-component/title-pages-component';
import {ActionIcon, ColumnDef, TableComponent} from '@/app/shared/components/table-component/table-component';
import {AppConstants} from '@/app/constants/AppConstants';
import {Campaign, PaginatedResponse} from '../../services/campaign.interface';
import {CampanasService} from '../../services/campanas.service';
import { SnackbarService } from '@/app/shared/services/snackbar.service';
import {ConfirmDeleteDialog} from '@/app/shared/components/confirm-delete-dialog/confirm-delete-dialog';
import {CampaingsStateService} from '../../services/campaing.state.service';
import {ClientStore} from '@/app/core/states/clientes-state/client-state.store';
import {Client} from '../../../cliente/models/cliente.interface';
import { ModalPasosCampana } from '@/app/shared/components/modal-pasos-campana/modal-pasos-campana';
import { DepartamentService } from '@/app/features/departamentos/services/departament.service';
import { OccupationsService } from '@/app/features/ocupaciones/services/occupations.service';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-campanas-component',
  standalone: true,
  imports: [
    TitlePagesComponent,
    TableComponent,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    ConfirmDeleteDialog,
    ModalPasosCampana
  ],
  templateUrl: './campanas-component.html',
  styleUrl: './campanas-component.scss',
})
export class CampanasComponent {
  protected readonly AppConstants = AppConstants;
  private router = inject(Router);
  private campanasService = inject(CampanasService);
  private campanasStateService = inject(CampaingsStateService);
  private store = inject(ClientStore);
  private snackbarService = inject(SnackbarService);
  private departamentService = inject(DepartamentService);
  private occupationsService = inject(OccupationsService);

  protected showBtn: boolean = true;
  protected campaigns: Campaign[] = [];
  protected client!: Client| null;

  protected showModal: boolean = false;
  protected campaignToDelete!: Campaign;
  protected message!: string;
  protected showModalPasosCampaign!: boolean;

  protected pageData = {
    title: AppConstants.CAMPANAS_TITLE,
    subTitle: AppConstants.CAMPANAS_SUBTITLE,
  }

  ngOnInit(){
    this.client = this.store.client();
    if(this.client){
      // Cargar campañas directamente sin validaciones
      this.loadCampaigns();
    }
  }

  private loadCampaigns(): void {
    // Cargar campañas (siempre, tanto en vista solo como en modo normal)
    const cachedCampaigns = this.store.campaigns();
    if (cachedCampaigns && cachedCampaigns.length > 0) {
      this.campaigns = cachedCampaigns;
    } else {
      // Si no hay en cache, hacer la petición
      this.campanasService.getCampaigns(this.client!.publicId).subscribe((data: PaginatedResponse<Campaign>) => {
        this.campaigns = data.content;
        this.store.setCampaigns(this.campaigns); // Guardar en cache
      });
    }
  }

  addCampaing = () => {
    // Validar que todos los departamentos tengan al menos una ocupación antes de crear campaña
    if (!this.client) return;

    this.departamentService.getDepartaments(this.client.publicId).subscribe({
      next: (departments) => {
        if (!departments || departments.length === 0) {
          this.snackbarService.showInfo('Debe completar el paso 2: crear departamentos');
          this.showModalPasosCampaign = true;
          return;
        }

        // Usar forkJoin para verificar ocupaciones en paralelo
        const occupationsRequests = departments.map(department =>
          this.occupationsService.getOccupations(this.client!.publicId, department.id.toString())
        );

        forkJoin(occupationsRequests).subscribe({
          next: (occupationsArray) => {
            // Verificar que TODOS los departamentos tengan al menos una ocupación
            const departmentsWithoutOccupations = departments.filter((dept, index) =>
              !occupationsArray[index] || occupationsArray[index].length === 0
            );

            if (departmentsWithoutOccupations.length > 0) {
              this.snackbarService.showInfo(`Los siguientes departamentos no tienen ocupaciones: ${departmentsWithoutOccupations.map(d => d.name).join(', ')}`);
              this.showModalPasosCampaign = true;
              return;
            }

            // Si pasaron las validaciones, navegar a crear campaña
            this.router.navigate(['/clientes/campañas/crear']);
          },
          error: (error) => {
            console.error('Error obteniendo ocupaciones:', error);
            this.snackbarService.showError('Error al verificar ocupaciones del cliente');
          }
        });
      },
      error: (error) => {
        console.error('Error obteniendo departamentos:', error);
        this.snackbarService.showError('Error al verificar departamentos del cliente');
      }
    });
  }

  editCampaing = (campaing: Campaign) => {
    this.campanasStateService.setCampaign(campaing);
    this.router.navigate(['/clientes/campañas/editar']);
  };

  protected actionsIcons: ActionIcon[] = [
    {
      label: 'Ver tokens',
      icon: 'verToken.svg',
      action: (c: Campaign) => this.verTokens(c),
    },
  ];

  verTokens(campaign: Campaign) {
    // guardamos la campaña en el state (igual que al editar)
    this.campanasStateService.setCampaign(campaign);
    // navegamos a la pantalla de edición con un flag para abrir la vista de tokens
    this.router.navigate(['/clientes/campañas/ver-tokens']);
  }

  //elimina el registro de cliente
  handleDelete  () {
    this.campanasService.deleteCampaign(this.campaignToDelete.publicId).subscribe({
      next: () => {
        this.campaigns = this.campaigns.filter(c => c.publicId !== this.campaignToDelete.publicId);
        this.snackbarService.showSuccess('Campaña eliminada con éxito');
        this.showModal = false;
      },
      error: (err) => {
        this.snackbarService.showError('Error al eliminar la campaña: ' + err.message);
      }
    });
  };

  //muestra el modal de confirmación de eliminación
  showConfirmDeleteDialog = (campaing: Campaign) => {
    //mensaje para la confirmación de eliminacion
    this.message = "Se eliminará la campaña: " + campaing.name;
    this.campaignToDelete = campaing;
    this.showModal = true;
  };

  //cierra el modal de confirmación de eliminación
  handleCancelDeleteDialog(){
    this.showModal = false;
  }

  closeModalPasosCampaign(): void {
    this.showModalPasosCampaign = false;
    this.router.navigate(['/clientes/departamento']);
  }

  columnDefinitions: ColumnDef[] = [
    { key: 'name', displayName: 'Nombre' },
    { key: 'description', displayName: 'Descripción'},
    { key: 'startDate', displayName: 'Inicio', type: 'date' },
    { key: 'endDate', displayName: 'Fin', type: 'date' },
    { key: 'acciones', displayName: 'Acciones'},
  ];

}
